import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';

// Feld-Fehler, für die es einen Satz gibt, den man am Tresen lesen kann.
// Was hier nicht steht, bleibt beim technischen Code — lieber ein
// Fachbegriff als ein erfundener Satz, der die Ursache verfehlt.
//
// Schlüssel ist `<feld>:<code>` aus `error_fields`, so wie das Backend
// ihn liefert (api/lib/validate.php und die Endpoints daneben).
const FELD_FEHLER_TEXT: Record<string, string> = {
  // Seit Migration 024 ist bonus_kundennr eindeutig. Ohne diesen Satz
  // sähe der Tresen nur "Speichern fehlgeschlagen: invalid_input" und
  // müsste raten, welches der sieben Felder gemeint ist.
  'bonus_kundennr:bereits_vergeben':
    'Diese BONUS-Nummer gehört bereits zu einem anderen Kunden.'
};

/**
 * Der Satz, der im Fehler-Toast steht.
 *
 * Reine Funktion, damit sie ohne Hook prüfbar ist.
 */
export function patchFehlerText(err: unknown): string {
  if (err instanceof ApiError) {
    // A46: Kein stiller Fehlschlag und kein nackter Fehlercode. Der
    // Zustand `bereit` gibt es für eine Versand-Bestellung nicht — wer
    // ihn trotzdem schickt (alte Ansicht im Browser, zweite Sitzung,
    // Aufruf von Hand), soll den Grund lesen und nicht raten, warum der
    // Chip zurückspringt.
    if (err.code === 'zustand_nicht_moeglich') {
      return 'Bei Versand gibt es „Bereit" nicht — die Bestellung geht von „Eingetroffen" direkt auf „Versendet".';
    }
    const felder = err.daten?.error_fields;
    if (felder !== null && typeof felder === 'object') {
      for (const [feld, code] of Object.entries(felder as Record<string, unknown>)) {
        const text = FELD_FEHLER_TEXT[`${feld}:${String(code)}`];
        if (text) return text;
      }
    }
    return `Speichern fehlgeschlagen: ${err.code}`;
  }
  return 'Speichern fehlgeschlagen: unbekannt';
}

// Generischer Helper fuer Bestellungs-PATCH-Endpoints (SPEC-011).
// Path ist der Pfad ab /api/, z.B. '/bestellungen/zustand.php'.
// Optional: showSuccessToast=false unterdrueckt das Erfolgs-Toast,
// nuetzlich fuer optimistische Checklist-Updates die ihre eigene UX haben.
type Options<TBody> = {
  path: string;
  bestellnr: string;
  showSuccessToast?: boolean;
  /**
   * Text der Erfolgsmeldung nach dem Muster "{Ding} {Verb}." — also
   * "Termin gespeichert." statt eines nackten "Gespeichert" (B79).
   * Ohne Angabe bleibt es beim allgemeinen Satz.
   *
   * Als Funktion, wenn der Wortlaut vom Vorgang abhaengt: ein geleertes
   * Datum ist ein Entfernen, kein Speichern (A9).
   */
  erfolgsText?: string | ((body: TBody) => string);
  optimistic?: {
    queryKey: readonly unknown[];
    apply: (cache: unknown, body: TBody) => unknown;
  };
};

export function usePatchBestellung<TBody, TResp = unknown>(opts: Options<TBody>) {
  const qc = useQueryClient();
  const toast = useToast();
  const showSuccessToast = opts.showSuccessToast ?? true;

  return useMutation<TResp, Error, TBody, { previous: unknown }>({
    mutationFn: (body) =>
      apiFetch<TResp>(opts.path, {
        method: 'POST',
        body: JSON.stringify(body)
      }),
    onMutate: async (body) => {
      if (!opts.optimistic) return { previous: undefined };
      await qc.cancelQueries({ queryKey: opts.optimistic.queryKey });
      const previous = qc.getQueryData(opts.optimistic.queryKey);
      qc.setQueryData(opts.optimistic.queryKey, (cache: unknown) =>
        opts.optimistic!.apply(cache, body)
      );
      return { previous };
    },
    onError: (err, _body, ctx) => {
      if (opts.optimistic && ctx) {
        qc.setQueryData(opts.optimistic.queryKey, ctx.previous);
      }
      toast('error', patchFehlerText(err));
    },
    onSuccess: (resp, body) => {
      if (!showSuccessToast) return;
      // Die Endpoints antworten mit {unchanged:true}, wenn der neue Wert
      // dem alten entspricht. Dann ist "gespeichert" schlicht unwahr (B75).
      const unveraendert =
        typeof resp === 'object' &&
        resp !== null &&
        (resp as { unchanged?: boolean }).unchanged === true;
      if (unveraendert) {
        toast('success', 'Nichts geändert.');
        return;
      }
      const text =
        typeof opts.erfolgsText === 'function' ? opts.erfolgsText(body) : opts.erfolgsText;
      toast('success', text ?? 'Änderung gespeichert.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['bestellung', opts.bestellnr] });
      qc.invalidateQueries({ queryKey: ['bestellungen'] });
    }
  });
}
